/**
 * Quick-add trigger that works outside a product card, and never navigates.
 *
 * QuickAddComponent resolves the product URL by walking up to <product-card>.
 * Inside the cart drawer there is none, so productPageUrl returns '' and the
 * modal opens empty. This subclass overrides that one getter and reuses the
 * rest - fetching, caching, morphing, variant sync.
 *
 * The click listener sits on the HOST element, not on the buttons. The drawer
 * is re-rendered by cart-items-component on every cart change, which patches
 * the inner markup. Listeners bound to individual buttons would be lost the
 * first time that happened while the host survived, leaving "Välj" dead with
 * no error anywhere. Delegating from the host survives any inner re-render.
 *
 * The cart drawer renders its own copy of 'quick-add-modal' so the dialog is
 * always in the DOM, including on pages with no product grid. Ours sits inside
 * [data-bb-fallback-wrapper] and is removed when the page already has a real one.
 */

import { QuickAddComponent } from '@theme/quick-add';

const OURS = '[data-bb-fallback-wrapper]';

/** Drop our fallback dialog if the page already has a real one. */
function dedupeDialogs() {
  const dialogs = [...document.querySelectorAll('quick-add-dialog')];
  if (dialogs.length < 2) return;

  const native = dialogs.find((dialog) => !dialog.closest(OURS));
  if (!native) return;

  for (const dialog of dialogs) {
    if (dialog.closest(OURS)) dialog.remove();
  }
}

class BbQuickAdd extends QuickAddComponent {
  #wired = false;

  get productPageUrl() {
    return this.dataset.productUrl || '';
  }

  connectedCallback() {
    super.connectedCallback();
    dedupeDialogs();

    if (this.#wired) return;
    this.addEventListener('click', this.#onHostClick);
    this.#wired = true;
  }

  #onHostClick = (event) => {
    const target = event.target;
    if (!(target instanceof Element)) return;

    const trigger = target.closest('[data-bb-choose]');
    if (!trigger || !this.contains(trigger)) return;

    event.preventDefault();

    // No navigation, ever. If the dialog is somehow missing the click is a
    // no-op rather than a trip to the product page.
    if (!document.getElementById('quick-add-dialog')) {
      console.warn('[bb-quick-add] quick-add-dialog not found; ignoring click');
      return;
    }

    this.handleClick(event);
  };
}

if (!customElements.get('bb-quick-add')) {
  customElements.define('bb-quick-add', BbQuickAdd);
}
